import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Container,
  Segment,
  Header,
  Icon,
  Grid,
  Label,
  Message,
  Button,
  Popup,
  Table,
  Input,
  Modal,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import moment from "moment";
import { PDFViewer } from "@react-pdf/renderer";

import { db } from "../firebase/firebaseConfig";
import { getDocs, collection, where, query } from "firebase/firestore";
import { UserContext } from "../context/UserContext";

import ExportarExcel from "../funciones/ExportarExcel";
import SimplePDF from "./SimplePDF";

const COLORS = {
  teal: "#00b5ad",
  tealSoft: "#e8f8f7",
  blue: "#2185d0",
  blueSoft: "#edf6fc",
  green: "#21ba45",
  greenSoft: "#edf9f0",
  orange: "#f2711c",
  red: "#db2828",
  border: "#e5e7eb",
  text: "#1f2937",
  muted: "#6b7280",
  background: "#f8fafc",
};

const cardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0,0,0,.06)",
};

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#374151",
  whiteSpace: "nowrap",
};

const safeText = (v) => {
  if (v === null || v === undefined) return "";
  if (v?.toDate) return moment(v.toDate()).format("DD/MM/YYYY HH:mm");

  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  return String(v);
};

const fmt = (ts) =>
  ts?.toDate ? moment(ts.toDate()).format("DD/MM/YYYY HH:mm") : "";

const ReportesST_PRO = () => {
  const { users } = useContext(UserContext);

  // base data
  const [ingresos, setIngresos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [buscador, setBuscador] = useState("");
  const [buscadorDebounced, setBuscadorDebounced] = useState("");

  // filtros
  const [fEstado, setFEstado] = useState("");
  const [fServicio, setFServicio] = useState("");

  // PDF
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [datospdf, setDatosPdf] = useState([]);
  const [test, setTest] = useState([]);
  const [testGpdf, setTestGpdf] = useState([]);
  const [userAdd, setUsersAdd] = useState([]);

  // ---------------- Debounce buscador ----------------
  useEffect(() => {
    const t = setTimeout(() => setBuscadorDebounced(buscador), 250);
    return () => clearTimeout(t);
  }, [buscador]);

  // ---------------- Load data ----------------
  const loadIngresos = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "ingresostcab"),
      where("emp_id", "==", users.emp_id)
    );

    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
    setIngresos(data);
  }, [users?.emp_id]);

  const loadUsuarios = useCallback(async () => {
    if (!users?.emp_id) return;

    const qU = query(
      collection(db, "usuarios"),
      where("emp_id", "==", users.emp_id)
    );

    const snap = await getDocs(qU);
    setUsuarios(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  }, [users?.emp_id]);

  useEffect(() => {
    const init = async () => {
      if (!users?.emp_id) return;

      setLoading(true);

      try {
        await Promise.all([loadIngresos(), loadUsuarios()]);
      } catch (error) {
        console.error("Error cargando órdenes:", error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [users?.emp_id, loadIngresos, loadUsuarios]);

  // ---------------- Diccionario tecnico correo -> nombre ----------------
  const tecnicoByCorreo = useMemo(() => {
    const map = {};

    (usuarios || []).forEach((u) => {
      if (u?.correo) {
        map[u.correo] = `${u.nombre || ""} ${u.apellido || ""}`.trim();
      }
    });

    return map;
  }, [usuarios]);

  // ---------------- Base (sin anulados + orden) ----------------
  const baseData = useMemo(() => {
    const sinAnulados = (ingresos || []).filter((e) => e.estado !== "ANULADO");
    return [...sinAnulados].sort((a, b) => (a.folio ?? 0) - (b.folio ?? 0));
  }, [ingresos]);

  // ---------------- Filtrado ----------------
  const filteredData = useMemo(() => {
    const q = (buscadorDebounced || "").trim().toLowerCase();

    // No mostrar resultados hasta escribir
    if (!q) return [];

    let arr = baseData;

    if (fEstado) {
      arr = arr.filter((x) => String(x.estado || "") === fEstado);
    }

    if (fServicio) {
      arr = arr.filter(
        (x) => String(x.servicionew || x.servicio || "") === fServicio
      );
    }

    return arr.filter((item) =>
      Object.keys(item).some((k) =>
        safeText(item[k]).toLowerCase().includes(q)
      )
    );
  }, [baseData, buscadorDebounced, fEstado, fServicio]);

  // ---------------- KPIs ----------------
  const kpis = useMemo(() => {
    const total = baseData.length;
    const activos = baseData.filter(
      (x) => x.estado && x.estado !== "ANULADO"
    ).length;
    const confirmados = baseData.filter((x) => x.confirmado === true).length;

    return { total, activos, confirmados };
  }, [baseData]);

  // ---------------- Excel ----------------
  const ExportarXls = () => {
    if (filteredData.length === 0) return;

    const columnsToShow = [
      "folio",
      "rut",
      "entidad",
      "correo",
      "telefono",
      "familia",
      "tipo",
      "modelo",
      "serie",
      "servicio",
      "estado",
      "fecha_out",
      "proximaMantencion",
      "diasParaMantencion",
      "estadoMantencion",
    ];

    const columnHeaders = {
      folio: "Folio",
      rut: "RUT",
      entidad: "Entidad",
      correo: "Correo",
      telefono: "Teléfono",
      familia: "Familia",
      tipo: "Tipo",
      modelo: "Modelo",
      serie: "Serie",
      servicio: "Servicio",
      estado: "Estado",
      fecha_out: "Fecha Salida",
      proximaMantencion: "Próxima Mantención",
      diasParaMantencion: "Días para Mantención",
      estadoMantencion: "Estado Mantención",
    };

    const dataParaExcel = filteredData.map((item) => {
      const out = { ...item };

      const nombreServicio = String(
        item.servicionew || item.servicio || ""
      ).trim();

      out.servicio = nombreServicio;

      if (item.fecha_out?.toDate) {
        const fechaSalida = item.fecha_out.toDate();
        out.fecha_out = fechaSalida.toLocaleDateString("es-CL");

        const esMantencionPreventiva =
          nombreServicio.toUpperCase() === "MANTENCION PREVENTIVA";

        if (esMantencionPreventiva) {
          const proximaMantencion = new Date(fechaSalida);
          proximaMantencion.setFullYear(proximaMantencion.getFullYear() + 1);

          out.proximaMantencion =
            proximaMantencion.toLocaleDateString("es-CL");

          const hoy = new Date();
          hoy.setHours(0, 0, 0, 0);
          proximaMantencion.setHours(0, 0, 0, 0);

          const diferenciaMs =
            proximaMantencion.getTime() - hoy.getTime();

          const diasParaMantencion = Math.ceil(
            diferenciaMs / (1000 * 60 * 60 * 24)
          );

          out.diasParaMantencion = diasParaMantencion;

          if (diasParaMantencion < 0) {
            out.estadoMantencion = "VENCIDA";
          } else if (diasParaMantencion === 0) {
            out.estadoMantencion = "VENCE HOY";
          } else if (diasParaMantencion <= 30) {
            out.estadoMantencion = "PRÓXIMA";
          } else {
            out.estadoMantencion = "A TIEMPO";
          }
        } else {
          out.proximaMantencion = "";
          out.diasParaMantencion = "";
          out.estadoMantencion = "";
        }
      } else {
        out.fecha_out = "";
        out.proximaMantencion = "";
        out.diasParaMantencion = "";
        out.estadoMantencion = "";
      }

      return out;
    });

    const excelBlob = ExportarExcel(
      dataParaExcel,
      columnsToShow,
      columnHeaders
    );

    const excelURL = URL.createObjectURL(excelBlob);
    const a = document.createElement("a");

    const esMantencionPreventiva =
      filteredData.length > 0 &&
      filteredData.every(
        (item) =>
          String(item.servicionew || item.servicio || "")
            .trim()
            .toUpperCase() === "MANTENCION PREVENTIVA"
      );

    a.href = excelURL;
    a.download = esMantencionPreventiva
      ? "MantencionesPreventivas.xlsx"
      : "OrdenesHistorial.xlsx";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(excelURL), 2000);
  };

  // ---------------- PDF ----------------
  const verPdf = useCallback(
    async (id) => {
      if (!users?.emp_id) return;

      setPdfLoading(true);
      setOpenPdf(true);

      try {
        const row = baseData.filter((x) => x.id === id);
        setDatosPdf(row);

        const qTest = query(
          collection(db, "testingreso"),
          where("emp_id", "==", users.emp_id),
          where("id_cab_inst", "==", id)
        );

        const qTestG = query(
          collection(db, "testevaluacion"),
          where("emp_id", "==", users.emp_id),
          where("id_cab_inst", "==", id)
        );

        const [snapTest, snapTestG] = await Promise.all([
          getDocs(qTest),
          getDocs(qTestG),
        ]);

        const t1 = snapTest.docs.map((d) => ({ ...d.data(), id: d.id }));
        const t2 = snapTestG.docs.map((d) => ({ ...d.data(), id: d.id }));

        setTest(
          [...t1].sort((a, b) => (a.fechamod ?? 0) - (b.fechamod ?? 0))
        );

        setTestGpdf(
          [...t2].sort((a, b) => (a.fechamod ?? 0) - (b.fechamod ?? 0))
        );

        const useraddCorreo = row?.[0]?.useradd;
        setUsersAdd(usuarios.filter((u) => u.correo === useraddCorreo));
      } catch (e) {
        console.error("Error al cargar datos para el PDF:", e);
      } finally {
        setPdfLoading(false);
      }
    },
    [users?.emp_id, baseData, usuarios]
  );

  const clearAll = () => {
    setBuscador("");
    setBuscadorDebounced("");
    setFEstado("");
    setFServicio("");
  };

  // Días transcurridos
  const calcDias = (item) => {
    const a = item?.date?.toDate ? item.date.toDate() : null;
    const b = item?.fechamod?.toDate ? item.fechamod.toDate() : null;

    if (!a || !b) return "";

    const diff = Math.floor((b - a) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const estadoColor = (estado) => {
    const e = String(estado || "").toUpperCase();

    if (["ENTREGADO", "REALIZADO", "CERRADO"].includes(e)) return "green";
    if (["ASIGNADO", "EN PROCESO", "EN_PROCESO"].includes(e)) return "blue";
    if (["PENDIENTE", "POR CONFIRMAR"].includes(e)) return "orange";

    return "grey";
  };

  return (
    <Container
      fluid
      style={{
        background: COLORS.background,
        minHeight: "100vh",
        padding: "18px 16px 35px",
      }}
    >
      <Container fluid style={{ maxWidth: 1400 }}>

        {/* CABECERA */}
        <Segment style={{ ...cardStyle, padding: "18px 20px", marginBottom: 16 }}>
          <Grid stackable verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={12}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 8,
                      background: COLORS.tealSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      name="search"
                      size="large"
                      style={{ color: COLORS.teal, margin: 0 }}
                    />
                  </div>

                  <div>
                    <Header
                      as="h2"
                      style={{ margin: 0, color: COLORS.text, fontSize: 21 }}
                    >
                      Búsqueda y seguimiento de órdenes
                      <Header.Subheader style={{ marginTop: 5 }}>
                        Consulte el historial y estado de las órdenes de Servicio Técnico
                      </Header.Subheader>
                    </Header>
                  </div>
                </div>
              </Grid.Column>

              <Grid.Column width={4} textAlign="right">
                <Popup
                  content="Exportar resultados a Excel"
                  trigger={
                    <Button
                      color="green"
                      icon
                      labelPosition="left"
                      onClick={ExportarXls}
                      disabled={filteredData.length === 0}
                    >
                      <Icon name="file excel" />
                      Exportar
                    </Button>
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* KPI */}
        <Grid stackable columns={3} style={{ marginBottom: 8 }}>
          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{ background: COLORS.tealSoft, color: COLORS.teal }}
                >
                  <Icon name="clipboard list" />
                </Label>
                <div>
                  <div style={{ color: COLORS.muted, fontSize: 12 }}>
                    Total órdenes
                  </div>
                  <strong style={{ fontSize: 23, color: COLORS.text }}>
                    {kpis.total}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{ background: COLORS.blueSoft, color: COLORS.blue }}
                >
                  <Icon name="heartbeat" />
                </Label>
                <div>
                  <div style={{ color: COLORS.muted, fontSize: 12 }}>
                    Órdenes activas
                  </div>
                  <strong style={{ fontSize: 23, color: COLORS.text }}>
                    {kpis.activos}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment style={{ ...cardStyle, margin: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Label
                  size="large"
                  style={{ background: COLORS.greenSoft, color: COLORS.green }}
                >
                  <Icon name="check circle outline" />
                </Label>
                <div>
                  <div style={{ color: COLORS.muted, fontSize: 12 }}>
                    Confirmadas
                  </div>
                  <strong style={{ fontSize: 23, color: COLORS.text }}>
                    {kpis.confirmados}
                  </strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* BUSCADOR */}
        <Segment style={{ ...cardStyle, marginTop: 12 }}>
          <Grid stackable verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={13}>
                <Header as="h4" style={{ marginBottom: 10 }}>
                  <Icon name="filter" color="teal" />
                  <Header.Content>
                    Búsqueda de órdenes
                    <Header.Subheader>
                      Folio, RUT, cliente, serie, estado, técnico u otro dato
                    </Header.Subheader>
                  </Header.Content>
                </Header>

                <Input
                  fluid
                  focus
                  placeholder="Escriba para buscar..."
                  value={buscador}
                  onChange={(e) => setBuscador(e.target.value)}
                  icon={{
                    name: buscador ? "close" : "search",
                    link: true,
                    onClick: () => (buscador ? setBuscador("") : null),
                  }}
                />
              </Grid.Column>

              <Grid.Column width={3} textAlign="right">
                <Button
                  basic
                  fluid
                  icon
                  labelPosition="left"
                  onClick={clearAll}
                  disabled={!buscador && !fEstado && !fServicio}
                >
                  <Icon name="eraser" />
                  Limpiar
                </Button>
              </Grid.Column>
            </Grid.Row>

            {buscadorDebounced.trim() && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Message info size="small" style={{ margin: 0 }}>
                    <Icon name="info circle" />
                    Mostrando <b>{filteredData.length}</b> resultado(s) para{" "}
                    <b>{buscadorDebounced}</b>.
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* RESULTADOS */}
        <Segment raised style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "13px 16px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Header as="h4" style={{ margin: 0 }}>
              <Icon name="list alternate outline" color="teal" />
              <Header.Content>
                Resultados
                <Header.Subheader>
                  {buscadorDebounced.trim()
                    ? `${filteredData.length} orden(es) encontrada(s)`
                    : "Ingrese un criterio de búsqueda"}
                </Header.Subheader>
              </Header.Content>
            </Header>

            {filteredData.length > 0 && (
              <Label basic color="teal">
                {filteredData.length} resultado(s)
              </Label>
            )}
          </div>

          {loading ? (
            <div style={{ height: 220, position: "relative" }}>
              <Dimmer active inverted>
                <Loader size="large">Procesando...</Loader>
              </Dimmer>
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
                maxWidth: "100%",
                overflowY: "auto",
                maxHeight: 520,
              }}
            >
              <Table
                celled
                striped
                selectable
                compact="very"
                singleLine
                style={{ fontSize: 11, margin: 0 }}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={stickyTh}>Orden</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Cliente</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Fecha ingreso</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Ingresado por</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Equipo</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Modelo</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Serie</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Servicio</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Estado</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Fecha estado</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh}>Técnico</Table.HeaderCell>
                    <Table.HeaderCell style={stickyTh} textAlign="center">
                      Días
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {filteredData.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell textAlign="center">
                        <Popup
                          content="Ver Orden de Ingreso"
                          trigger={
                            <Button
                              basic
                              compact
                              size="mini"
                              onClick={() => verPdf(item.id)}
                              style={{
                                color: COLORS.red,
                                background: "#fff5f5",
                                boxShadow: "none",
                              }}
                            >
                              <Icon name="file pdf outline" color="red" />
                              {item.folio}
                            </Button>
                          }
                        />
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                        }}
                        title={item.entidad}
                      >
                        {item.entidad || "-"}
                      </Table.Cell>

                      <Table.Cell style={{ whiteSpace: "nowrap" }}>
                        {fmt(item.date) || "-"}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.useradd || "-"}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.tipo}
                      >
                        {item.tipo || "-"}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.modelo}
                      >
                        {item.modelo || "-"}
                      </Table.Cell>

                      <Table.Cell>{item.serie || "-"}</Table.Cell>

                      <Table.Cell
                        style={{
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.servicionew || item.servicio}
                      >
                        {item.servicionew || item.servicio || "-"}
                      </Table.Cell>

                      <Table.Cell>
                        <Label basic size="tiny" color={estadoColor(item.estado)}>
                          {item.estado || "-"}
                        </Label>
                      </Table.Cell>

                      <Table.Cell style={{ whiteSpace: "nowrap" }}>
                        {item.fechamod ? fmt(item.fechamod) : "Pendiente"}
                      </Table.Cell>

                      <Table.Cell style={{ whiteSpace: "nowrap" }}>
                        {tecnicoByCorreo[item.tecnico] || "-"}
                      </Table.Cell>

                      <Table.Cell textAlign="center">
                        {calcDias(item)}
                      </Table.Cell>
                    </Table.Row>
                  ))}

                  {filteredData.length === 0 && buscadorDebounced.trim() && (
                    <Table.Row>
                      <Table.Cell colSpan={12} textAlign="center">
                        <div style={{ padding: 30, color: COLORS.muted }}>
                          <Icon name="search minus" size="large" />
                          <div style={{ marginTop: 8, fontWeight: 600 }}>
                            Sin resultados
                          </div>
                          <div style={{ marginTop: 3 }}>
                            No se encontraron órdenes para "{buscadorDebounced}".
                          </div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {!buscadorDebounced.trim() && (
                    <Table.Row>
                      <Table.Cell colSpan={12} textAlign="center">
                        <div style={{ padding: 30, color: COLORS.muted }}>
                          <Icon name="search" size="large" />
                          <div style={{ marginTop: 8, fontWeight: 600 }}>
                            Busque una orden
                          </div>
                          <div style={{ marginTop: 3 }}>
                            Escriba en el buscador para ver resultados.
                          </div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          )}
        </Segment>

        {/* MODAL PDF */}
        <Modal
          open={openPdf}
          onClose={() => setOpenPdf(false)}
          size="large"
          closeIcon
        >
          <Modal.Header>
            <Icon name="file pdf outline" color="red" />
            Orden de Ingreso
            {datospdf?.[0]?.folio && (
              <Label basic style={{ marginLeft: 10 }}>
                Folio {datospdf[0].folio}
              </Label>
            )}
          </Modal.Header>

          <Modal.Content style={{ padding: 0 }}>
            {pdfLoading ? (
              <div style={{ height: 560 }}>
                <Dimmer active inverted>
                  <Loader size="large">Procesando...</Loader>
                </Dimmer>
              </div>
            ) : (
              <PDFViewer
                width="100%"
                height="560"
                style={{ border: "1px solid #ddd" }}
              >
                <SimplePDF
                  props={datospdf}
                  test={test}
                  testGpdf={testGpdf}
                  usuario={userAdd}
                  empresa={users?.emp_id}
                />
              </PDFViewer>
            )}
          </Modal.Content>

          <Modal.Actions>
            <Button
              icon
              labelPosition="left"
              onClick={() => setOpenPdf(false)}
              color="teal"
            >
              <Icon name="close" />
              Cerrar
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    </Container>
  );
};

export default ReportesST_PRO;